import React, { useEffect, useRef, useState } from 'react';
import {
  FlatList,
  View,
  type LayoutChangeEvent,
  type ViewStyle,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Dimensions,
} from 'react-native';

type AutoScrollingFlatListProps<T> = {
  data: T[];
  renderItem: ({ item, index }: { item: T; index: number }) => React.ReactElement;
  horizontal?: boolean;
  width?:number,
  style?: ViewStyle;
  speed: number; // pixels per second
  minDuration?: number; // milliseconds (optional)
  onCompleteScroll?: (completed: boolean) => void;
  resetKey?: string | number; // used to reset internal state
};

const AutoScrollingFlatList = <T,>({
  data,
  renderItem,
  horizontal = false,
  style,
  speed,
  width,
  minDuration = 0,
  onCompleteScroll,
  resetKey,
}: AutoScrollingFlatListProps<T>) => {
  const listRef = useRef<FlatList<T>>(null);
  const itemSizes = useRef<(number | undefined)[]>([]);
  const totalSize = useRef(0);
  const offset = useRef(0);
  const scrollTimer = useRef<NodeJS.Timeout | null>(null);
  const scrollStartTime = useRef<number | null>(null);

  const [ready, setReady] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [containerSize, setContainerSize] = useState(0);

  const handleLayout = (e: LayoutChangeEvent) => {
    const size = horizontal
      ? e.nativeEvent.layout.width
      : e.nativeEvent.layout.height;
    setContainerSize(size);
  };

  const updateItemLayout = (index: number, e: LayoutChangeEvent) => {
    const size = horizontal
      ? e.nativeEvent.layout.width
      : e.nativeEvent.layout.height;

    if(size == 0){ return }
    itemSizes.current[index] = size;
    const allMeasured = itemSizes.current.length === data.length && itemSizes.current.every((v): v is number => typeof v === 'number');
    if (allMeasured && !ready) {
      totalSize.current = itemSizes.current
        .filter((v): v is number => typeof v === 'number')
        .reduce((acc, val) => acc + val, 0);
      setReady(true);
    }
      
  };

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentOffset = horizontal
      ? e.nativeEvent.contentOffset.x
      : e.nativeEvent.contentOffset.y;
    offset.current = currentOffset;
  };

  useEffect(() => {
    if (!ready || isPaused || speed <= 0 || containerSize === 0) return;
    if(totalSize.current == 0){ return }
    const frameInterval = 16;
    const pixelsPerFrame = (speed * frameInterval) / 1000;
    scrollStartTime.current = Date.now();

    scrollTimer.current = setInterval(() => {
      offset.current += pixelsPerFrame;
      const maxOffset = totalSize.current - containerSize;
      const now = Date.now();
      const reachedEnd = offset.current >= maxOffset;
      const minTimeElapsed = now - (scrollStartTime.current ?? 0) >= minDuration;

      if (reachedEnd) {
        if (minTimeElapsed) {
          offset.current = 0;
          scrollStartTime.current = Date.now();
          listRef.current?.scrollToOffset({ offset: 0, animated: false });
          if (onCompleteScroll) onCompleteScroll(true);
        } else {
          offset.current = maxOffset;
        }
      } else {
        listRef.current?.scrollToOffset({
          offset: offset.current,
          animated: false,
        });
      }
    }, frameInterval);

    return () => {
      if (scrollTimer.current) clearInterval(scrollTimer.current);
    };
  }, [ready, speed, isPaused, containerSize, onCompleteScroll, minDuration]);

  // Kick off scroll once ready
  useEffect(() => {
    if (ready && !isPaused && speed > 0 && containerSize > 0) {
      listRef.current?.scrollToOffset({ offset: offset.current, animated: false });
    }
  }, [ready, isPaused, speed, containerSize]);

  // Reset state/refs on resetKey change
  useEffect(() => {
    if (scrollTimer.current) clearInterval(scrollTimer.current);
    offset.current = 0;
    scrollStartTime.current = null;
    itemSizes.current = [];
    totalSize.current = 0;
    setReady(false);
    setIsPaused(false);
  }, [resetKey]);

  // Ensure auto-scroll works after first mobile touch
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const unlock = () => {
      setIsPaused(false);
    };

    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const wrappedRenderItem = ({ item, index }: { item: T; index: number }) => (
    <View onLayout={(e) => updateItemLayout(index, e)} style={{ flexShrink: 0 }}>
      {renderItem({ item, index })}
    </View>
  );

  const wrapperProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsPaused(true),
          onMouseLeave: () => setIsPaused(false),
          onTouchStart: () => {
            if (scrollStartTime.current !== null) {
              setIsPaused(true);
            }
          },
          onTouchEnd: () => setIsPaused(false),
        }
      : {
          onTouchStart: () => {
            if (scrollStartTime.current !== null) {
              setIsPaused(true);
            }
          },
          onTouchEnd: () => setIsPaused(false),
        };

  const windowSize = Dimensions.get('window');
  return (
    <View
      {...(wrapperProps as any)}
      onLayout={handleLayout}
      style={[
        {
          overflow: 'hidden', flex:1,
          height: horizontal ? '100%' : windowSize.height,
          width: horizontal ? width ?? windowSize.width : '100%',
        },
        style,
      ]}
    >
      <FlatList
        ref={listRef}
        data={data}
        renderItem={wrappedRenderItem}
        keyExtractor={(_, i) => i.toString()}
        horizontal={horizontal}
        scrollEnabled={true}
        initialNumToRender={data.length}
        //removeClippedSubviews={true}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default AutoScrollingFlatList;
